import { useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Maximize2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, Popup, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './CommandDeck.css'; 
import { sectionVariants } from '../omsDashTheme';
import { computeOperationalHealth } from '../utils/deriveCommand';
import { deriveBriefing } from '../utils/deriveBriefing';
import type { OmsLayerKpiHealth, OmsMapLocation, OmsOverviewResponse } from '../types';

/** Register health colours: dot/fill + darker ink for text on cream. */
const SITE_HEALTH: Record<OmsLayerKpiHealth, { color: string; ink: string; label: string }> = {
  good: { color: '#339274', ink: '#1F5A47', label: 'Performing' },
  attention: { color: '#C9A961', ink: '#8A5A08', label: 'Watch' },
  critical: { color: '#9F1239', ink: '#9F1239', label: 'Critical' },
  neutral: { color: '#5A6275', ink: '#5A6275', label: 'No activity' },
};

const INK = '#2A1F17';
const TITLE_GREEN = '#0B3B2E';
const CAPTION = '#7C8A82';
const CHAMPAGNE_DEEP = '#8A5A08';

interface Props {
  data: OmsOverviewResponse | null;
  loading: boolean;
  userName: string;
  /** Map ↔ Explorer sync: clicking a site drills the Explorer to its state. */
  onFocusState?: (state: string) => void;
}

/**
 * Site marker: health-coloured core sized by project volume, with the
 * project count engraved inside so the map reads as data, not just dots.
 */
function markerIcon(site: OmsMapLocation): L.DivIcon {
  const color = SITE_HEALTH[site.health].color;
  const size = site.projectCount >= 8 ? 22 : site.projectCount >= 3 ? 19 : 16;
  const count = site.projectCount > 1 ? site.projectCount : '';
  return L.divIcon({
    className: 'oms-holo-marker-wrap',
    html: `<span class="oms-holo-marker" style="--holo:${color};width:${size}px;height:${size}px">
      <span class="oms-holo-ring"></span>
      <span class="oms-holo-ring oms-holo-ring--slow"></span>
      <span class="oms-holo-core">${count}</span>
    </span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

/**
 * Frame all site markers (clamped to a country-level zoom, nudged right of
 * the welcome veil). Called on load and whenever the deck resizes.
 */
function fitAllSites(map: L.Map, sites: OmsMapLocation[]) {
  if (sites.length === 0) return;
  const bounds = L.latLngBounds(sites.map((s) => [s.latitude, s.longitude] as [number, number]));
  let zoom = sites.length === 1 ? 6.2 : map.getBoundsZoom(bounds.pad(0.25), false);
  zoom = Math.min(Math.max(zoom, 5), 6.8);
  const size = map.getSize();
  const centerPoint = map
    .project(bounds.getCenter(), zoom)
    .subtract(new L.Point(size.x * 0.17, 0));
  map.setView(map.unproject(centerPoint, zoom), zoom, { animate: false });
}

function ViewController({ sites, mapRef }: {
  sites: OmsMapLocation[];
  mapRef: MutableRefObject<L.Map | null>;
}) {
  const map = useMap();
  mapRef.current = map;
  useEffect(() => {
    fitAllSites(map, sites);
  }, [map, sites]);
  return null;
}

function SiteCard({ site }: { site: OmsMapLocation }) {
  const health = SITE_HEALTH[site.health];
  return (
    <div className="min-w-[210px] text-[11px] leading-snug">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-extrabold" style={{ color: TITLE_GREEN }}>{site.name}</span>
        <span
          className="rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em]"
          style={{ color: health.ink, backgroundColor: `${health.color}24` }}
        >
          {health.label}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 font-semibold" style={{ color: CAPTION }}>
        <span>Projects</span>
        <span className="text-right tabular-nums" style={{ color: INK }}>{site.projectCount}</span>
        <span>Engineers working</span>
        <span className="text-right tabular-nums" style={{ color: INK }}>{site.resourceCount}</span>
        <span>Capacity</span>
        <span className="text-right tabular-nums" style={{ color: INK }}>
          {site.capacityMW > 0 ? `${site.capacityMW} MW` : '—'}
        </span>
        <span>Tasks done</span>
        <span className="text-right tabular-nums" style={{ color: INK }}>
          {site.tasks.completed} of {site.tasks.total} tasks
        </span>
        <span>Pending · Overdue</span>
        <span className="text-right tabular-nums" style={{ color: INK }}>
          {site.tasks.pending} · {site.tasks.overdue} tasks
        </span>
      </div>
      {site.topProjects.length > 0 && (
        <p className="mt-2 border-t border-[rgba(11,59,46,0.15)] pt-1.5" style={{ color: CAPTION }}>
          {site.topProjects.join(' · ')}
          {site.projectCount > site.topProjects.length ? ` +${site.projectCount - site.topProjects.length} more` : ''}
        </p>
      )}
    </div>
  );
}

/**
 * Command Deck — an engraved atlas of live operations on the register's
 * cream paper. Every geo-tagged site pulses with its health colour;
 * clicking a site zooms into its state and opens the intel card with a
 * link to the per-location dashboard. Frameless: the map feathers into
 * the page with no visible boundary.
 */
export default function CommandDeck({ data, loading, userName, onFocusState }: Props) {
  const navigate = useNavigate();
  const sites = useMemo(() => data?.map ?? [], [data]);
  const health = computeOperationalHealth(data);
  const liveDot = SITE_HEALTH[health.health];

  const mapRef = useRef<L.Map | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Time-aware courtesy greeting — personal without being showy.
  // Morning starts at 05:00: a manager opening the deck past midnight is
  // still in their evening, not tomorrow's morning.
  const hour = new Date().getHours();
  const greeting =
    hour >= 5 && hour < 12 ? 'Good morning' : hour >= 12 && hour < 17 ? 'Good afternoon' : 'Good evening';
  /** The dashboard's one-line briefing, derived from his live portfolio. */
  const briefing = useMemo(() => deriveBriefing(data), [data]);

  // Resize the leaflet canvas when the deck expands/collapses, then re-frame
  // the full fleet view for the new canvas size.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const timer = setTimeout(() => {
      map.invalidateSize();
      if (expanded) {
        map.scrollWheelZoom.enable();
      } else {
        map.scrollWheelZoom.disable();
        map.closePopup();
      }
      fitAllSites(map, sites);
    }, 60);
    return () => clearTimeout(timer);
  }, [expanded, sites]);

  // Esc exits full view; page scroll is locked while expanded.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [expanded]);

  /** Project counts rolled up per state for the presence summary. */
  const stateSummary = useMemo(() => {
    const byState = new Map<string, number>();
    for (const s of sites) {
      const key = (s.state ?? '').trim() || 'Unmapped';
      byState.set(key, (byState.get(key) ?? 0) + s.projectCount);
    }
    return [...byState.entries()]
      .map(([state, projects]) => ({ state, projects }))
      .sort((a, b) => b.projects - a.projects);
  }, [sites]);

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={0}
      aria-label="OMS command deck"
      className={
        expanded
          ? 'fixed inset-0 z-[2000] bg-[#F2EADB]'
          : 'oms-holo-hero oms-holo-frameless relative overflow-hidden'
      }
    >
      <div className={`oms-holo-map relative w-full ${expanded ? 'oms-map-expanded h-full' : 'h-[340px] sm:h-[320px]'}`}>
        {sites.length > 0 && (
          <MapContainer
            className="absolute inset-0 h-full w-full"
            center={[22.5, 79]}
            zoom={5}
            minZoom={4}
            maxZoom={9}
            zoomControl={false}
            scrollWheelZoom={false}
            attributionControl
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            />
            <ZoomControl position="topright" />
            <ViewController sites={sites} mapRef={mapRef} />
            {sites.map((site) => (
              <Marker
                key={site.locationId}
                position={[site.latitude, site.longitude]}
                icon={markerIcon(site)}
                ref={(marker) => {
                  // Popups open on RIGHT click only — unbind leaflet's
                  // default left-click popup opener.
                  if (marker) marker.off('click', (marker as any)._openPopup, marker);
                }}
                eventHandlers={{
                  // Clicking a site = clicking its state card in the Explorer.
                  // Inside full view the click opens the intel popup instead.
                  click: (e) => {
                    if (expanded) {
                      (e.target as L.Marker).openPopup();
                      return;
                    }
                    const state = (site.state ?? '').trim();
                    if (state && onFocusState) onFocusState(state);
                    else setExpanded(true);
                  },
                  contextmenu: (e) => (e.target as L.Marker).openPopup(),
                }}
              >
                <Popup className="oms-holo-pop" closeButton offset={[0, -8]} maxWidth={280}>
                  <SiteCard site={site} />
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/oms/locations/${site.locationId}`)}
                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.1em] text-white transition hover:bg-[#2d5c47]"
                    style={{ backgroundColor: '#264f3c', border: '1px solid rgba(11,59,46,0.45)' }}
                  >
                    Open location dashboard <ArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}

        {/* Hairline engraved graticule */}
        <div aria-hidden className="oms-holo-scan pointer-events-none absolute inset-0 z-[420]" />

        {/* Left veil — solid cream gradient for legibility, no blur */}
        <div aria-hidden className="oms-holo-veil pointer-events-none absolute inset-y-0 left-0 z-[430] w-full sm:w-[52%] lg:w-[44%]" />

        {/* Left content — vertically centred masthead, no dead space */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-[440] flex w-full flex-col justify-center p-5 sm:w-[52%] lg:w-[44%]">
          <p className="oms-holo-eyebrow text-[10px] font-bold uppercase tracking-[0.24em]" style={{ color: CHAMPAGNE_DEEP }}>
            OMS Command Center
          </p>
          <p className="oms-holo-title mt-3 text-[15px] font-semibold leading-none" style={{ color: '#1F5A47' }}>
            {greeting},
          </p>
          <h1 className="oms-holo-title mt-1 text-[26px] font-extrabold leading-tight lg:text-[30px]">
            {userName || 'Operations Lead'}
          </h1>
          {/* Quiet champagne underline — an engraved signature stroke. */}
          <span
            aria-hidden
            className="mt-1.5 block h-[2px] w-11 rounded-full"
            style={{ background: 'linear-gradient(to right, #C9A961 0%, rgba(201,169,97,0) 100%)' }}
          />
          {/* The dashboard speaks — a daily portfolio briefing in one warm line. */}
          <p className="mt-2 max-w-[420px] text-[12px] italic leading-snug" style={{ color: '#5A6275' }}>
            “{briefing}”
          </p>
          <p className="mt-2.5 flex items-center gap-2 text-xs font-medium" style={{ color: CAPTION }}>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ backgroundColor: liveDot.color }} />
              <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: liveDot.color }} />
            </span>
            {data?.generatedAt
              ? `Live · updated ${new Date(data.generatedAt).toLocaleString()}`
              : loading
                ? 'Connecting to operations feed…'
                : 'Operations feed unavailable'}
          </p>

          {stateSummary.length > 0 && (
            <p className="pointer-events-auto mt-4 max-w-full border-t border-[rgba(11,59,46,0.12)] pt-3 text-[11px] font-semibold leading-relaxed">
              {stateSummary.map((item, i) => (
                <span key={item.state} className="whitespace-nowrap">
                  {i > 0 && <span style={{ color: 'rgba(11,59,46,0.28)' }}>{'  ·  '}</span>}
                  <span style={{ color: '#1F5A47' }}>{item.state}</span>{' '}
                  <span className="font-extrabold tabular-nums" style={{ color: CHAMPAGNE_DEEP }}>{item.projects}</span>{' '}
                  <span style={{ color: CAPTION }}>project{item.projects === 1 ? '' : 's'}</span>
                </span>
              ))}
            </p>
          )}
        </div>

        {/* Full-view toggle */}
        {!expanded && sites.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="absolute bottom-4 right-4 z-[500] flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide shadow-[0_8px_20px_-14px_rgba(42,31,23,0.4)] transition hover:bg-[rgba(31,90,71,0.08)]"
            style={{ backgroundColor: 'rgba(250,247,238,0.92)', border: '1px solid rgba(11,59,46,0.3)', color: TITLE_GREEN }}
          >
            <Maximize2 className="h-3 w-3" /> Full map
          </button>
        )}
        {/* Full-view exit + interaction hint */}
        {expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="absolute right-5 top-5 z-[500] flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold shadow-[0_8px_20px_-14px_rgba(42,31,23,0.4)] transition hover:bg-[rgba(31,90,71,0.08)]"
            style={{ backgroundColor: 'rgba(250,247,238,0.96)', border: '1px solid rgba(11,59,46,0.3)', color: TITLE_GREEN }}
          >
            <X className="h-3.5 w-3.5" /> Exit full view
          </button>
        )}
        {/* Empty / loading fallback where the map would be */}
        {sites.length === 0 && (
          <div className="absolute inset-y-0 right-0 z-[410] hidden w-1/2 items-center justify-center sm:flex">
            <p className="max-w-[260px] text-center text-xs font-semibold" style={{ color: CAPTION }}>
              {loading ? 'Scanning site locations…' : 'No geo-tagged sites yet — add coordinates to project locations to light up the map.'}
            </p>
          </div>
        )}

      </div>
    </motion.section>
  );
}
